import React, { useEffect, useMemo } from "react";
import { View, TextInput, StyleSheet } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import {
  setObservationNotes,
  setSelectedObservation,
} from "../../store/slices/observationSlice";
import LHSectionHeader from "../../components/LHSectionHeader";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { getLocalTimestamp } from "../../api/apiClient";
import { useTheme } from "../../theme";
import type { ThemeColors } from "../../theme/types";
const PulseCheckNotes: React.FC = () => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const selectedObservation = useSelector(
    (state: RootState) => state.observation.selectedObservation
  );
  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );
  const selectedFilters = useSelector(
    (state: RootState) => state.observation.selectedFilters
  );
  const selectedRecord = useSelector(
    (state: RootState) => state.pulseCheck.selectedRecord
  );

  // Get observation notes either from selectedObservation or directly from existing record
  const observationNotes =
    selectedObservation?.observationNotes ||
    selectedRecord?.observationData?.observationNotes ||
    "";

  const isCompleted =
    selectedObservation?.status === "completed" || pulseState === "completed";

  // Initialize selectedObservation if it doesn't exist
  useEffect(() => {
    if (!selectedObservation) {
      const currentDate = getLocalTimestamp();

      console.log('selectedRecord', selectedRecord);
      // Create a new observation object with proper structure
      const newObservation = {
        id: selectedRecord?.id || null,
        type: selectedFilters.observationType || "",
        employee: selectedRecord?.employee || selectedFilters.employee || "",
        details: "",
        created: selectedRecord?.created || currentDate,
        by: selectedRecord?.by || "",
        modifiedBy: selectedRecord?.modifiedBy || "",
        period: selectedRecord?.period || "",
        action: selectedRecord?.action || "",
        observationNotes:
          selectedRecord?.observationData?.observationNotes || "",
        status: selectedRecord?.status || "",
      };

      dispatch(setSelectedObservation(newObservation));
    } else if (
      selectedRecord?.observationData?.observationNotes &&
      !selectedObservation.observationNotes &&
      selectedRecord?.observationData?.observationNotes !==
        selectedObservation.observationNotes
    ) {
      // If we have notes in the selectedRecord but not in selectedObservation, update it
      dispatch(
        setObservationNotes(selectedRecord.observationData.observationNotes)
      );
    }
  }, [selectedObservation, selectedRecord, selectedFilters, dispatch]);

  const handleNoteChange = (text: string) => {
    // Log for debugging
    console.log("Note changed to:", text);
    dispatch(setObservationNotes(text));
  };

  return (
    <View style={styles.cardContainer}>
      <LHSectionHeader
        title="Observation Notes"
        containerStyle={styles.shiftHeader}
        textStyle={styles.sectionHeaderText}
      />
      <View style={styles.detailBox}>
        <TextInput
          style={[styles.notesInput, isCompleted && styles.disabledInput]}
          placeholder="Please record any notes from any selected boxes."
          placeholderTextColor={colors.text.secondary}
          multiline
          numberOfLines={4}
          value={observationNotes}
          onChangeText={handleNoteChange}
          editable={!isCompleted}
          testID="observation-notes-input"
        />
      </View>
    </View>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      marginBottom: 16,
      borderRadius: 6,
      overflow: 'hidden',
      elevation: 4,
      shadowColor: 'rgba(0, 94, 192, 0.08)',
      shadowOffset: {
        width: 4,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    shiftHeader: {
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    sectionHeaderText: {
      fontSize: 16,
      color: "white",
    },
    detailBox: {
      padding: 12,
      backgroundColor: colors.background.surface,
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      borderWidth: 1,
      borderTopWidth: 0,
      borderColor: colors.border.default,
    },
    notesInput: {
      borderWidth: 1,
      borderColor: colors.input.border,
      borderRadius: 6,
      padding: 12,
      minHeight: 100,
      fontSize: 14,
      color: colors.text.primary,
      textAlignVertical: "top",
      backgroundColor: colors.input.background,
    },
    // Kept as exact literal values to preserve
    // tests/screens/PulseCheck/PulseCheckNotes.test.tsx assertion
    // (disabled input backgroundColor === "#DADADA").
    disabledInput: {
      backgroundColor: "#DADADA",
      borderColor: "#B1B1B1",
      color: "#3A3A3A",
    },
  });

export default PulseCheckNotes;
